import React, { useEffect, useState } from "react";
import { User, Building2, Bell, CreditCard, Save, Mail, CheckCircle2, Sparkles } from "lucide-react";
import { endpoints } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import ChannelPicker from "../components/ChannelPicker";
import RetentionTemplatesSection from "../components/RetentionTemplatesSection";
import BillingSection from "../components/BillingSection";
import { toast } from "sonner";

const TABS = [
  { key: "profile", label: "Profile", icon: User },
  { key: "gym", label: "Gym", icon: Building2 },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "retention", label: "Retention", icon: Sparkles },
  { key: "billing", label: "Billing", icon: CreditCard },
];

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const TIMEZONES = [
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London",
  "America/New_York", "America/Los_Angeles", "UTC",
];

const REMINDER_TYPES = [
  { key: "welcome_channels", label: "Welcome (new lead)", desc: "Sent when a new lead is added." },
  { key: "follow_up_channels", label: "Follow-up", desc: "Manual nudges to contacted leads." },
  { key: "trial_reminder_channels", label: "Trial reminder", desc: "Day before a scheduled trial." },
  { key: "renewal_channels", label: "Renewal reminder", desc: "7d / 2d / same-day before expiry." },
  { key: "inactivity_channels", label: "Inactivity nudge", desc: "Members who haven't visited lately." },
];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("profile");
  const [settings, setSettings] = useState(null);
  const [gym, setGym] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      endpoints.getSettings(),
      endpoints.getGym().catch(() => ({ data: null })),
    ])
      .then(([s, g]) => { setSettings(s.data); setGym(g.data); })
      .finally(() => setLoading(false));
  }, []);

  const saveSettings = async (next) => {
    setSaving(true);
    setSettings(next);
    try {
      await endpoints.updateSettings(next);
      toast.success("Saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const saveGym = async (updates) => {
    if (!gym) return;
    try {
      const { data } = await endpoints.updateGym(updates);
      setGym(data);
      toast.success("Gym updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  if (loading || !settings) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  const isOwner = user?.role === "owner";

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-4xl mx-auto">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground/70 mb-2">Preferences</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground" data-testid="settings-title">
          Settings
        </h1>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-8 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`tab-${key}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <section className="space-y-6" data-testid="profile-panel">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-muted text-xl font-medium text-muted-foreground flex items-center justify-center">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div>
              <div className="font-display text-lg font-semibold text-foreground">{user?.name}</div>
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <Mail className="w-3.5 h-3.5" /> {user?.email}
              </div>
              <span className={`inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
                user?.role === "owner" ? "bg-primary text-white" : "bg-muted text-foreground/85"
              }`}>
                {user?.role || "member"}
              </span>
            </div>
          </div>
          <div className="border border-border rounded-xl p-5 text-xs text-muted-foreground bg-muted/30">
            Profile name, email and picture are managed by your Google account. Sign in with a different Google account to update.
          </div>
        </section>
      )}

      {tab === "gym" && (
        <section className="space-y-5" data-testid="gym-panel">
          <div className="border border-border rounded-xl p-5 space-y-4 bg-card">
            <h3 className="font-display text-base font-semibold text-foreground">Gym details</h3>
            <div>
              <Label>Gym name</Label>
              <Input
                data-testid="settings-gym-name"
                value={settings.gym_name || ""}
                onChange={e => setSettings({ ...settings, gym_name: e.target.value })}
                onBlur={() => saveSettings(settings)}
                disabled={!isOwner}
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                data-testid="settings-gym-address"
                value={settings.gym_address || ""}
                onChange={e => setSettings({ ...settings, gym_address: e.target.value })}
                onBlur={() => saveSettings(settings)}
                disabled={!isOwner}
                placeholder="123, MG Road, Bengaluru"
              />
            </div>
            <div>
              <Label>Default waiver / liability form URL</Label>
              <Input
                type="url"
                data-testid="settings-waiver-url"
                value={settings.default_waiver_url || ""}
                onChange={e => setSettings({ ...settings, default_waiver_url: e.target.value })}
                onBlur={() => saveSettings(settings)}
                disabled={!isOwner}
                placeholder="https://forms.google.com/..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Paste a Google Form / Jotform link. Members get a one-tap link from their profile to sign before training.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Timezone</Label>
                <Select
                  value={settings.gym_timezone || "Asia/Kolkata"}
                  onValueChange={(v) => saveSettings({ ...settings, gym_timezone: v })}
                  disabled={!isOwner}
                >
                  <SelectTrigger data-testid="settings-timezone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency</Label>
                <Select
                  value={settings.currency || "INR"}
                  onValueChange={(v) => saveSettings({ ...settings, currency: v })}
                  disabled={!isOwner}
                >
                  <SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="border border-border rounded-xl p-5 space-y-4 bg-card">
            <h3 className="font-display text-base font-semibold text-foreground">Default plan</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Plan name</Label>
                <Input
                  data-testid="settings-plan-name"
                  value={settings.default_plan_name || ""}
                  onChange={e => setSettings({ ...settings, default_plan_name: e.target.value })}
                  onBlur={() => saveSettings(settings)}
                  disabled={!isOwner}
                />
              </div>
              <div>
                <Label>Amount</Label>
                <Input
                  type="number"
                  data-testid="settings-plan-amount"
                  value={settings.default_plan_amount || 0}
                  onChange={e => setSettings({ ...settings, default_plan_amount: parseFloat(e.target.value) || 0 })}
                  onBlur={() => saveSettings(settings)}
                  disabled={!isOwner}
                />
              </div>
              <div>
                <Label>Days</Label>
                <Input
                  type="number"
                  data-testid="settings-plan-days"
                  value={settings.default_plan_duration_days || 30}
                  onChange={e => setSettings({ ...settings, default_plan_duration_days: parseInt(e.target.value) || 30 })}
                  onBlur={() => saveSettings(settings)}
                  disabled={!isOwner}
                />
              </div>
            </div>
          </div>

          <div className="border border-border rounded-xl p-5 bg-card">
            <h3 className="font-display text-base font-semibold text-foreground mb-3">At-risk threshold</h3>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                className="w-24"
                data-testid="settings-at-risk"
                value={settings.at_risk_days || 10}
                onChange={e => setSettings({ ...settings, at_risk_days: parseInt(e.target.value) || 10 })}
                onBlur={() => saveSettings(settings)}
                disabled={!isOwner}
              />
              <span className="text-sm text-muted-foreground">days without visit → flagged at-risk</span>
            </div>
          </div>

          {!isOwner && <p className="text-xs text-muted-foreground/70 italic">Only owners can edit gym settings.</p>}
        </section>
      )}

      {tab === "notifications" && (
        <section className="space-y-5" data-testid="notifications-panel">
          <div className="border border-border rounded-xl p-5 bg-card">
            <h3 className="font-display text-base font-semibold text-foreground mb-1">Default channels</h3>
            <p className="text-xs text-muted-foreground mb-5">Pick which channels to suggest for each message type. Individual messages can still override the default.</p>
            <ul className="divide-y divide-border/60">
              {REMINDER_TYPES.map(({ key, label, desc }) => (
                <li key={key} className="py-4 flex items-start justify-between gap-4" data-testid={`notif-row-${key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground">{label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
                  </div>
                  <div className="shrink-0">
                    <ChannelPicker
                      value={settings[key] || ["whatsapp"]}
                      onChange={(next) => saveSettings({ ...settings, [key]: next })}
                      testidPrefix={`pick-${key}`}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-start gap-3 p-4 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-amber-900">
            <Bell className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">Delivery (MVP)</div>
              <p className="text-xs mt-0.5 text-amber-800/80">
                WhatsApp opens wa.me; <strong>SMS and Email are logged locally only</strong> in this MVP - no real delivery. Integrate a provider (Twilio / Resend) later to send for real.
              </p>
            </div>
          </div>
        </section>
      )}

      {tab === "retention" && (
        <RetentionTemplatesSection
          settings={settings}
          onSaved={async () => {
            try {
              const { data } = await endpoints.getSettings();
              setSettings(data);
            } catch {}
          }}
        />
      )}

      {tab === "billing" && (
        <BillingSection />
      )}
    </div>
  );
}
